/**
 * Add an ad to one line item from the campaign card (A7, M2): the wizard's Ad step
 * (`AdStep`) and the wizard's run (`lib/ad-run.ts`), under a PAUSED line item.
 *
 * 🔴 **Offered only while the line item is PAUSED, and refused here as well as disabled**
 * — the Creative panel's rule (its own, decided 2026-10-08): a promoted tweet arrives
 * ACTIVE with no PUT to switch it off, and the worker refuses the attach with 409 under
 * an ACTIVE line item. The status checked is the row the card read; the card re-reads
 * after the run, so a line item switched on in between is seen rather than assumed.
 *
 * 🔴 **One run per draft, and the lock is the wizard's**: once anything was made or may
 * exist, Attach stays off — a second press would make a second tweet or a second
 * promoted tweet. The run owns no state; the states are shown as the wizard shows them
 * (`stepText`, `RUN_ROWS`), and the parent is told once the attach is reported.
 *
 * 🔴 **The run is held in `lib/create-locks.ts` under the line item, not in this form**:
 * the form is mounted only while Add ad is open on that row, so closing it, another row
 * or the card closing used to clear the run — and Attach was offered again after a tweet
 * was made or a promote may have been. A run that ended with the ad attached is let go
 * when the form is opened next, so the next ad on the same line item is a new draft, as
 * it was; within one opening the button stays off after it, as before.
 */
import { useEffect, useRef, useState } from "react";
import { type AdDraft, runAd } from "../lib/ad-run.ts";
import type { LineItem, LineItemObjective, XAccount } from "../lib/api.ts";
import { api } from "../lib/api.ts";
import {
	ADD_AD_DONE,
	ADD_AD_HELP,
	ADD_AD_PAUSED_ONLY,
	ADD_AD_STOPPED,
	ATTACH_LABEL,
} from "../lib/campaign-manage-copy.ts";
import { RUN_ROWS, problemText, stepText } from "../lib/create-ad-copy.ts";
import {
	type Run,
	type StepState,
	adProblem,
	cardMode,
	createdSoFar,
	freshRun,
	mayCreateAgain,
	runStopped,
	somethingMayExist,
} from "../lib/create-ad.ts";
import { addAdLockKey, finishedRun, useHeldOutcome } from "../lib/create-locks.ts";
import { Button, InfoBanner, Spinner } from "../ui/index.ts";
import { AdStep } from "./AdStep.tsx";

interface Props {
	lineItem: LineItem;
	account: XAccount;
	/** Told once the attach has been reported, done or not: the card re-reads the line item's ads. */
	onAttached: () => void;
}

export function AddAd({ lineItem, account, onAttached }: Props) {
	const [draft, setDraft] = useState<AdDraft>({
		adMode: "write",
		adText: "",
		adName: "",
		adFiles: [],
		adUrl: "",
		// Under WEBSITE_CLICKS the card is offered first, as in the wizard (`newDraft`).
		adWebsiteAs: "card",
		cardTitle: "",
		cardImage: null,
		adReplies: "",
		tweet: null,
	});
	const set = (patch: Partial<AdDraft>) => setDraft((d) => ({ ...d, ...patch }));
	const [tried, setTried] = useState(false);
	const [run, setRun] = useHeldOutcome<Run>(addAdLockKey(account.xAccountId, lineItem.id));
	// biome-ignore lint/correctness/useExhaustiveDependencies: once, when the form opens — a run that attached its ad is done with; one that stopped or is still sending keeps the lock
	useEffect(() => {
		if (run !== null && finishedRun(run)) setRun(null);
	}, []);
	const [creating, setCreating] = useState(false);
	const held = useRef<Run | null>(null);
	const update = (patch: Partial<Run>) => {
		const next = { ...(held.current ?? freshRun([])), ...patch };
		held.current = next;
		setRun(next);
	};
	const mediaAt = (i: number, state: StepState) => {
		const rows = (held.current ?? freshRun([])).media.map((m, j) =>
			j === i ? { ...m, state } : m,
		);
		update({ media: rows });
	};

	// The objective is X's own value on the row, which may be one v1 does not offer on
	// create; the URL rule applies only to the one it names.
	const objective: LineItemObjective =
		lineItem.objective === "WEBSITE_CLICKS" ? "WEBSITE_CLICKS" : "ENGAGEMENTS";
	const paused = lineItem.entityStatus === "PAUSED";
	const problem = adProblem({ ...draft, objective });
	const locked = run !== null && !mayCreateAgain(run);

	async function attach() {
		if (!paused || creating || locked) return;
		if (problem !== null) {
			setTried(true);
			return;
		}
		setTried(false);
		held.current = freshRun(
			[],
			draft.adFiles,
			draft.adMode,
			cardMode({ objective, adMode: draft.adMode, adWebsiteAs: draft.adWebsiteAs }),
		);
		setRun(held.current);
		setCreating(true);
		try {
			await runAd(
				api,
				{ account: account.xAccountId, lineItemId: lineItem.id, objective, draft },
				{
					mediaAt,
					card: (state) => update({ card: state }),
					tweet: (state) => update({ tweet: state }),
					ad: (state) => update({ ad: state }),
					held: () => held.current ?? freshRun([]),
				},
			);
		} finally {
			setCreating(false);
			// 🔴 The parent re-reads whatever happened: a 502 can carry created, and the
			// status this screen checked against was read before the write.
			onAttached();
		}
	}

	const rows = run
		? [
				...run.media.map(
					(m, i) =>
						[`media-${i}-${m.name}`, `${RUN_ROWS.media} — ${m.name}`, "media", m.state] as const,
				),
				...(run.card === null ? [] : [["card", RUN_ROWS.card, "card", run.card] as const]),
				...(run.tweet === null ? [] : [["tweet", RUN_ROWS.tweet, "tweet", run.tweet] as const]),
				["ad", RUN_ROWS.ad, "ad", run.ad] as const,
			]
		: [];

	return (
		<div className="mt-3 grid gap-4 rounded-lg border border-line p-3">
			<h5 className="text-sm font-semibold text-content">
				Add ad — line item
				<span className="ml-2 font-mono text-xs text-muted">{lineItem.id}</span>
			</h5>
			<p className="text-sm text-muted">{ADD_AD_HELP}</p>
			<p className="text-sm text-muted">{ADD_AD_PAUSED_ONLY}</p>
			{!paused && (
				<InfoBanner variant="warning" title={`X reports this line item ${lineItem.entityStatus}.`}>
					{ADD_AD_PAUSED_ONLY}
				</InfoBanner>
			)}
			<AdStep xAccountId={account.xAccountId} objective={objective} draft={draft} set={set} />
			{tried && problem !== null && <InfoBanner variant="danger" title={problemText(problem)} />}
			<div>
				<Button
					type="button"
					loading={creating}
					disabled={!paused || locked}
					onClick={() => void attach()}
				>
					{ATTACH_LABEL}
				</Button>
			</div>
			{run && (
				<ol className="grid gap-2 rounded-lg border border-line p-3 text-sm">
					{rows.map(([key, label, what, state]) => {
						const line = stepText(what, state);
						return (
							<li key={key} className="grid gap-1">
								<span className="font-medium text-content">{label}</span>
								<span className="flex items-center gap-2 text-muted">
									{state.kind === "running" && <Spinner size="sm" />}
									{line.text}
								</span>
								{line.detail && <span className="text-xs text-muted">{line.detail}</span>}
							</li>
						);
					})}
				</ol>
			)}
			{run && runStopped(run) && (
				<InfoBanner variant="warning" title={ADD_AD_STOPPED.title}>
					{ADD_AD_STOPPED.body(createdSoFar(run), somethingMayExist(run))}
				</InfoBanner>
			)}
			{run?.ad.kind === "done" && (
				<InfoBanner variant="success" title={ADD_AD_DONE.title}>
					{ADD_AD_DONE.body}
				</InfoBanner>
			)}
		</div>
	);
}
